import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // SEO Integration

const StoreLocationPage = () => {
  const colors = {
    primary: "#FFAA00",
    secondary: "#8A8A89",
  };

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 font-sans">
      {/* --- SEO START --- */}
      <Helmet>
        <title>Notre Boutique à Salé | Electro Titan - Tabriquet</title>
        <meta 
          key="description" name="description" 
          content="Visitez le showroom Electro Titan à Salé (Tabriquet). Retrouvez nos équipements électriques et électroménagers. Itinéraire, horaires et conseils d'experts." 
        />
        <meta name="keywords" content="magasin électroménager Salé, Electro Titan Tabriquet, boutique électricité Salé, showroom électroménager Maroc" />
        <link rel="canonical" href={window.location.href} />

        {/* JSON-LD LocalBusiness Schema - Vital for Google Maps ranking */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ElectronicsStore",
            "name": "Electro Titan",
            "image": "https://www.titanelectro.ma/assets/images/logo.png",
            "url": "https://www.titanelectro.ma/adresse",
            "telephone": "+212640033322",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "8, Avenue allal al arbi al alaoui Hay Salam",
              "addressLocality": "Tabriquet",
              "addressRegion": "Salé",
              "postalCode": "11000",
              "addressCountry": "MA"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "34.0384", 
              "longitude": "-6.8016"
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "19:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "09:00",
                "closes": "15:00"
              }
            ],
            "sameAs": [
              "https://www.facebook.com/electrotitan",
              "https://www.instagram.com/electrotitan"
            ]
          })}
        </script>
      </Helmet>
      {/* --- SEO END --- */}

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="font-bold tracking-[0.3em] uppercase text-sm"
            style={{ color: colors.secondary }}
          >
            Visitez-nous
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-black text-gray-900 mt-4 mb-6 uppercase tracking-tight"
          >
            Notre Boutique à Salé
          </motion.h1>
          <div className="h-1.5 w-24 mx-auto rounded-full" style={{ backgroundColor: colors.primary }}></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-0 rounded-[2.5rem] overflow-hidden shadow-2xl bg-white border border-gray-100">
          
          {/* LEFT: CONTENT & INFO */}
          <div className="lg:w-5/12 p-8 lg:p-16 flex flex-col justify-center bg-white">
            <div className="space-y-10">
              
              {/* Address Box */}
              <section>
                <h2 className="text-xs font-black uppercase tracking-[0.2em] mb-4" style={{ color: colors.secondary }}>
                  Localisation
                </h2>
                <div className="flex items-start gap-4">
                  <div className="text-2xl" style={{ color: colors.primary }}>📍</div>
                  <address className="not-italic">
                    <p className="text-xl font-bold text-gray-900 leading-tight">
                      8, Avenue allal al arbi al alaoui Hay Salam
                    </p>
                    <p className="text-lg font-medium text-gray-500 uppercase tracking-wide">Tabriquet, Salé, Maroc</p>
                  </address>
                </div>
              </section>

              {/* Hours Box */}
              <section>
                <h2 className="text-xs font-black uppercase tracking-[0.2em] mb-4" style={{ color: colors.secondary }}>
                  Horaires d'ouverture
                </h2>
                <div className="grid grid-cols-2 gap-y-2 text-sm font-bold text-gray-700">
                  <span>Lundi - Vendredi</span>
                  <span className="text-right">09:00 - 19:00</span>
                  <span>Samedi</span>
                  <span className="text-right">09:00 - 15:00</span>
                  <span style={{ color: colors.primary }}>Dimanche</span>
                  <span className="text-right" style={{ color: colors.primary }}>Fermé</span>
                </div>
              </section>

              {/* Contact Box */}
              <section className="pt-6 border-t border-gray-100">
                <div className="flex flex-col gap-4">
                  <a href="https://maps.app.goo.gl/Mjwxp2unQhsGSMnh9" target='_blank' rel="noreferrer">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-4 rounded-xl font-bold uppercase tracking-widest shadow-lg text-white"
                      style={{ backgroundColor: colors.primary }}
                    >
                      Obtenir l'itinéraire
                    </motion.button>
                  </a>

                  <button onClick={() => navigate("/contact")}
                    className="w-full py-3 rounded-xl border-2 font-bold uppercase tracking-widest transition-all"
                    style={{ borderColor: colors.secondary, color: colors.secondary }}
                  >
                    Contactez Nous
                  </button>
                </div>
              </section>
            </div>
          </div>

          {/* RIGHT: INTERACTIVE MAP */}
          <div className="lg:w-7/12 h-[500px] lg:h-auto bg-gray-100 relative">
            <iframe
              title="Store Location Map Electro Titan"
              className="absolute inset-0 w-full h-full border-0 grayscale-[10%] hover:grayscale-0 transition-all duration-700"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d826.4641292807054!2d-6.7938339!3d34.0475515!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda76b5da396c1a1%3A0x426e0e666bad47f1!2sTitan%20Electro!5e0!3m2!1sfr!2sma!4v1770828534879!5m2!1sfr!2sma"
              allowFullScreen=""
              loading="lazy"
            ></iframe>
            
            <div className="hidden lg:block absolute bottom-8 left-8 bg-black/80 backdrop-blur-md p-6 rounded-2xl text-white max-w-xs border border-white/10">
              <p className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: colors.primary }}>
                Point de vente officiel
              </p>
              <p className="text-sm leading-relaxed opacity-80">
                Retrouvez toutes nos gammes de produits et bénéficiez de l'expertise de nos conseillers sur place.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Features Bar */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: "🛍️", title: "Click & Collect", desc: "Achetez en ligne, retirez en magasin" },
            { icon: "🛠️", title: "Service Après-Vente", desc: "Support technique et réparations" },
            { icon: "🤝", title: "Conseil Pro", desc: "Experts disponibles sans rendez-vous" }
          ].map((feature, idx) => (
            <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 text-center shadow-sm">
              <span className="text-3xl block mb-3" role="img" aria-label={feature.title}>{feature.icon}</span>
              <h3 className="font-bold text-gray-900 uppercase text-xs tracking-widest mb-1">{feature.title}</h3>
              <p className="text-sm text-gray-500">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StoreLocationPage;